<template>
  <div>
    <main class="instrument-selected-list">
      <div v-if="props.instruments.length > 0" class="instruments">
        <BaseText
          class="instrument__title"
          size="sm"
          as="p"
          :color="NebraskaColors.textSecondary"
          >Produtos selecionados</BaseText
        >
        <CardInstrumentSelected
          v-for="item in props.instruments"
          :key="item.id"
          :add-product="false"
          :instrument="item"
          :is-selected="Boolean(productSelected.includes(item.id))"
          @handle-instrument-click="handleInstrumentSelected"
        />
      </div>
    </main>
    <footer class="footer">
      <ButtonContainer v-if="!props.isDeleteActive">
        <template #button-primary>
          <ButtonPrimary
            size="large"
            :label="isEditor ? 'Salvar alterações' : createWalletButtonLabel"
            :loading="isLoading"
            @click="handleConfirmationEvent"
          />
        </template>
        <template #button-secondary>
          <ButtonSecondary
            size="large"
            label="Adicionar mais produtos"
            @click="$emit('show-wallet-selection')"
          />
        </template>
      </ButtonContainer>
      <ButtonContainer v-else>
        <template #button-primary>
          <ButtonPrimary
            size="large"
            label="Remover"
            :disabled="!productSelected.length"
            :loading="isLoadingRemove"
            @click="handleDeleteProduct"
          />
        </template>
      </ButtonContainer>
    </footer>
    <ModalCreateWallet
      data-testid="modal-create-wallet"
      :is-open-modal="showWalletModal"
      :is-template="isTemplate"
      @close="handleConfirmationEvent"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, inject, watch, computed } from 'vue';
import {
  BaseText,
  ButtonContainer,
  ButtonSecondary,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import ModalCreateWallet from '../Modals/ModalCreateWallet/ModalCreateWallet.vue';
import CardInstrumentSelected from './CardInstrumentSelected.vue';
import { Product } from '../../types';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  IDataConfirmEdit,
  IEditPortfolioResponse,
  IProductRemove,
  IProductsRemoveParam
} from '../../services/types/models/IBffInvestments';
import { useRouter } from 'vue-router/composables';

interface Props {
  instruments: Product[];
  isDeleteActive: boolean;
  isEditor: boolean;
  portfolioId?: string;
  isTemplate?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits(['show-wallet-selection', 'disable-delete-mode']);

const removeProductFromWallet = inject(
  'removeProductFromWallet',
  async (params: IProductsRemoveParam) => ({ params })
);

const confirmEditPortfolio = inject(
  'confirmEditPortfolio',
  async (_: IDataConfirmEdit) => ({ name: '' } as IEditPortfolioResponse)
);

watch(
  () => props.instruments,
  (newValue, _) => {
    if (newValue.length === 0) {
      emit('show-wallet-selection');
    }
  }
);

watch(
  () => props.isDeleteActive,
  (newValue, _) => {
    if (!newValue) {
      productSelected.value = [];
    }
  }
);

const router = useRouter();

const productSelected = ref<string[]>([]);

const showWalletModal = ref(false);

const isLoading = ref(false);

const isLoadingRemove = ref(false);

const createWalletButtonLabel = computed(() => {
  return props.isTemplate ? 'Criar carteira modelo' : 'Criar carteira';
});

async function handleConfirmationEvent() {
  if (props.isEditor) {
    isLoading.value = true;

    const result = await confirmEditPortfolio({
      portfolioId: props.portfolioId!
    });

    isLoading.value = false;

    router.replace({
      name: 'wealthWalletEdited',
      params: { id: props.portfolioId ?? '' },
      query: { portfolioname: result?.name || '-' }
    });
  } else {
    showWalletModal.value = !showWalletModal.value;
  }
}

function handleInstrumentSelected({ id }: IProductRemove) {
  if (props.isDeleteActive) {
    if (productSelected.value.includes(id)) {
      productSelected.value = productSelected.value.filter(item => item !== id);
    } else {
      productSelected.value.push(id);
    }
  }
}

async function handleDeleteProduct() {
  try {
    isLoadingRemove.value = true;
    await removeProductFromWallet({ productIds: productSelected.value });
  } finally {
    emit('disable-delete-mode');
    isLoadingRemove.value = false;
  }
}
</script>
<style lang="less" scoped>
.instruments {
  width: 100%;
  max-width: 1280px;
  margin-top: @size-spacing-x600;
  margin-bottom: @size-spacing-x2000;

  .instrument__title {
    font-weight: 700;
    text-transform: uppercase;
  }
}

.instrument-selected-list {
  display: flex;
  justify-content: center;
  padding: @size-spacing-x800;
}

.footer {
  position: fixed;
  bottom: 0;
  width: 100%;
  background-color: @background-secondary;
}
</style>
